import { FILTER_KEYS } from '../consts';
import useFilters from '../hooks/useFilters';
import { DateTypeArg } from '../types';
import { formatDate } from '../utils';
import DateInput from './DateInput';
import { useQuery } from '@tanstack/react-query';
import { getProducts, getOrderStatus } from 'api/requests/giftCard/giftCard';
import { useMemo, useCallback } from 'react';
import SingleSelect from 'shared/BIDS/components/SingleSelect';
import { ALL } from 'shared/consts/giftCards';
import useFilterSelectors from 'shared/hooks/useFilterSelectors';
import useMobileLayout from 'shared/hooks/useMobileLayout';
import { queryKeyBuilder } from 'shared/query';
import { isDefined } from 'shared/utils/bids';
import { isParameterAll } from 'shared/utils/giftCards';

function Header() {
  const isMobileLayout = useMobileLayout();
  const { onSingleFilterSelected, onPartialFilterSelected, onFiltersDeleted } = useFilterSelectors();

  const { status, createdDateFrom, createdDateTo, productId } = useFilters();

  const resetCallback = useCallback(
    () =>
      onFiltersDeleted(
        FILTER_KEYS.productId,
        FILTER_KEYS.status,
        FILTER_KEYS.createdDateFrom,
        FILTER_KEYS.createdDateTo
      ),
    [onFiltersDeleted]
  );

  const areFiltersModified = useMemo(() => {
    if (!isParameterAll(status) || !isParameterAll(productId) || isDefined(createdDateFrom) || isDefined(createdDateTo))
      return true;
    return false;
  }, [status, productId, createdDateFrom, createdDateTo]);

  const { data: productsData, isPending: areProductsPending } = useQuery({
    queryKey: queryKeyBuilder.giftCardsProducts(),
    queryFn: getProducts,
    select: (resp) => resp.data,
  });

  const { data: statusesData, isPending: areStatusesPending } = useQuery({
    queryKey: queryKeyBuilder.giftCardsStatuses(),
    queryFn: getOrderStatus,
    select: (response) => response.data,
  });

  const productsOptions = useMemo(() => {
    const resultingProducts = [
      {
        value: ALL,
        label: 'All Products',
      },
    ];
    productsData?.forEach((product) =>
      resultingProducts.push({
        value: String(product.productId),
        label: product.name,
      })
    );
    return resultingProducts;
  }, [productsData]);

  const statusesOptions = useMemo(() => {
    const resultingStatuses = [
      {
        value: ALL,
        label: 'All Statuses',
      },
    ];
    statusesData?.forEach((status) =>
      resultingStatuses.push({
        value: status.orderStatus,
        label: status.orderStatus,
      })
    );
    return resultingStatuses;
  }, [statusesData]);

  return (
    <div className={`flex items-center gap-4 ${isMobileLayout ? 'overflow-scroll no-scrollbar' : 'flex-wrap'}`}>
      <button
        disabled={!areFiltersModified}
        className="tracking-tight text-sm underline disabled:text-palette-light-grey text-palette-gold"
        onClick={resetCallback}
      >
        Reset
      </button>
      <div className="border-r border-palette-color-bg-grey w-px h-8" />
      <SingleSelect
        isLoading={areProductsPending}
        prefix="Product"
        options={productsOptions}
        value={productId}
        onChange={(v: string) => onSingleFilterSelected(FILTER_KEYS.productId, v)}
      />
      <SingleSelect
        isLoading={areStatusesPending}
        prefix="Status"
        options={statusesOptions}
        value={status}
        onChange={(v: string) => onSingleFilterSelected(FILTER_KEYS.status, v)}
      />
      <DateInput
        fromDate={createdDateFrom || null}
        toDate={createdDateTo || null}
        onChange={(dates: DateTypeArg) => {
          const [from, to] = dates;
          onPartialFilterSelected({
            [FILTER_KEYS.createdDateFrom]: from ? formatDate(from) : '',
            [FILTER_KEYS.createdDateTo]: to ? formatDate(to) : '',
          });
        }}
        onClear={() => {
          onPartialFilterSelected({ [FILTER_KEYS.createdDateFrom]: '', [FILTER_KEYS.createdDateTo]: '' });
        }}
      />
    </div>
  );
}

export default Header;
